import type { HelpArticleDef } from "./articleKit";
import {
  AppLink,
  ArticleLink,
  DeploymentNote,
  P,
  Sect,
  Steps,
  Tip,
  Ui,
} from "./articleKit";

export const GETTING_STARTED_ARTICLES: HelpArticleDef[] = [
  {
    slug: "getting-started",
    title: "Start here: your first hour in Obvious",
    category: "Getting started",
    minutes: 6,
    summary:
      "The setup order that works: profile, data, pipeline, first form. One hour, and the system is yours.",
    keywords: ["onboarding", "setup", "first steps", "new user", "begin", "tour"],
    related: ["importing-your-data", "contacts-and-companies", "whats-live-vs-deployment"],
    body: (
      <>
        <P>
          Obvious is a CRM built around one idea: every contact, deal, form
          fill, email, and dollar of pipeline should connect, so you always
          know what's working. This article walks the setup order that gets
          you productive in about an hour. Do the steps in order - each one
          makes the next one better.
        </P>
        <Tip>
          Joining a workspace someone else already set up? Steps 2 through 5
          are admin work you can skip. Read step 1, set your profile, then
          jump to step 6 and the guides for{" "}
          <ArticleLink slug="contacts-and-companies">
            Contacts and companies
          </ArticleLink>{" "}
          and{" "}
          <ArticleLink slug="deals-and-pipeline">
            Deals and the pipeline
          </ArticleLink>
          .
        </Tip>
        <Sect title="1. Find your way around">
          <P>
            The top navigation is the whole map.{" "}
            <AppLink to="/">Dashboard</AppLink> is your daily view.{" "}
            <AppLink to="/contacts">Contacts</AppLink>,{" "}
            <AppLink to="/companies">Companies</AppLink>, and{" "}
            <AppLink to="/deals">Deals</AppLink> are your records.{" "}
            <AppLink to="/tasks">Tasks</AppLink> is your to-do list and
            calendar, <AppLink to="/inbox">Inbox</AppLink> collects email
            conversations, <Ui>Reports</Ui> and <Ui>Marketing</Ui> open as
            menus with their own sections.
          </P>
          <Tip>
            Press <Ui>⌘K</Ui> (or <Ui>/</Ui>) anywhere to search every
            contact, company, and deal. It's the fastest way to move around,
            and most people stop using the nav for lookups within a day.
          </Tip>
        </Sect>
        <Sect title="2. Make it yours">
          <P>
            Open <AppLink to="/settings/profile">Settings → My profile</AppLink>{" "}
            and set your name and email. Then, if you're the admin, spend five
            minutes in{" "}
            <AppLink to="/settings/account-defaults">Account defaults</AppLink>:
            company identity, logo, and the workspace timezone every logged
            time displays in.
          </P>
        </Sect>
        <Sect title="3. Bring your data in">
          <P>
            Import contacts from a CSV with <Ui>Smart import</Ui> on the{" "}
            <AppLink to="/contacts">Contacts page</AppLink>: it maps your
            columns to Obvious fields (any export's headers work), dedupes by
            email, and can create custom fields on the fly. The full
            walkthrough, including the quick fixed-header importer and the
            JSON restore, lives in{" "}
            <ArticleLink slug="importing-your-data">
              Importing your data
            </ArticleLink>
            .
          </P>
        </Sect>
        <Sect title="4. Shape your pipeline">
          <P>
            The default deal stages (Opportunity → Proposal Sent → In
            Negotiation → Won/Lost, plus a Delayed parking stage) fit most
            services businesses, but they're yours to change at{" "}
            <AppLink to="/settings/objects">Settings → Objects</AppLink>. Each
            stage carries a win probability (20 / 40 / 60 / 10 for Delayed)
            that powers the weighted forecast on your reports, so keep those
            honest.
          </P>
        </Sect>
        <Sect title="5. Put your first form on your site">
          <P>
            The fastest way to see the whole system click is a lead form. Go
            to <AppLink to="/marketing">Marketing → Forms</AppLink>, open the
            starter "Contact us" form, and copy the embed code onto any page.
            Every fill creates or updates a contact, lands on their timeline
            with attribution, and can kick off automations. Details in{" "}
            <ArticleLink slug="forms">Forms</ArticleLink>.
          </P>
        </Sect>
        <Sect title="6. Learn your daily rhythm">
          <P>
            Day to day, you'll live on the{" "}
            <AppLink to="/">Dashboard</AppLink>: the "Keep an eye on" panel
            surfaces contacts heating up, deals going quiet, and overdue
            follow-ups (see{" "}
            <ArticleLink slug="keep-an-eye-on">
              the Keep an eye on guide
            </ArticleLink>
            ), and the Hot Contacts list ranks who deserves your next call.
            Work those, log what happened as notes and tasks, and the reports
            take care of themselves.
          </P>
        </Sect>
        <DeploymentNote>
          You may be reading this inside a preview workspace. Everything you
          see works, but outbound email, live form endpoints, and data syncs
          switch on when your workspace is deployed. The honest breakdown is
          in{" "}
          <ArticleLink slug="whats-live-vs-deployment">
            What's live now vs. at deployment
          </ArticleLink>
          .
        </DeploymentNote>
      </>
    ),
  },
  {
    slug: "whats-live-vs-deployment",
    title: "What's live now vs. at deployment",
    category: "Getting started",
    minutes: 3,
    summary:
      "Which parts run right now, which switch on when your workspace deploys, and how to tell the difference.",
    keywords: ["preview", "demo", "sample data", "activate", "go live", "deployment"],
    related: ["getting-started", "integrations", "email-templates-and-campaigns"],
    body: (
      <>
        <P>
          Obvious is honest about what's running. Anywhere you see a note like
          "activates at deployment" or "sample data", that feature's design
          and interface are final, but the live wiring (sending real email,
          receiving real form posts, syncing external accounts) turns on when
          your workspace is deployed to its own infrastructure.
        </P>
        <Sect title="Working right now">
          <P>
            Everything you build and record: contacts, companies, deals,
            notes, tags, tasks, custom fields, lifecycle stages, saved
            reports, dashboards, form designs, email templates, lists,
            sequences, workflow definitions, lead scoring rules, and every
            setting. Attribution reporting runs on the touch data your records
            carry, and lead scores compute live from your scoring rules.
          </P>
        </Sect>
        <Sect title="Switching on with deployment">
          <P>
            Outbound sending (campaigns and sequences actually emailing
            people), the public form endpoint your embeds post to, inbox sync,
            the BCC capture address, OAuth data syncs on the{" "}
            <AppLink to="/settings/integrations">Integrations</AppLink> page,
            and scheduled workflow execution. Until then, marketing screens
            simulate realistic activity so you can see exactly how the
            analytics will read, and label it as such.
          </P>
        </Sect>
        <Sect title="How to tell at a glance">
          <P>
            Look for two markers: an amber rocket callout in articles like
            this one, and a footnote under any table or card whose numbers are
            simulated. If a screen has neither, what you're looking at is
            real, saved data.
          </P>
        </Sect>
      </>
    ),
  },
  {
    slug: "importing-your-data",
    title: "Importing your data",
    category: "Getting started",
    minutes: 5,
    summary:
      "Three ways in: Smart import (maps any CSV, dedupes by email), the quick CSV importer, and a JSON restore. Plus how to clean up duplicates.",
    keywords: [
      "csv", "import", "migrate", "spreadsheet", "upload", "excel",
      "hubspot", "salesforce", "apollo", "duplicate", "merge", "dedupe",
      "json", "export", "backup",
    ],
    related: ["getting-started", "contacts-and-companies", "settings-and-administration"],
    body: (
      <>
        <P>
          Most teams arrive with a spreadsheet or an old CRM export. Both
          import paths live on the{" "}
          <AppLink to="/contacts">Contacts page</AppLink>, in the toolbar
          above the list. For almost everyone, <Ui>Smart import</Ui> is the
          right door.
        </P>
        <Sect title="Smart import: any CSV, mapped and deduped">
          <Steps
            items={[
              <>
                Export your existing data to CSV, one row per person, with a
                header row. Any column names are fine - HubSpot, Salesforce,
                Apollo, ZoomInfo exports all work as-is.
              </>,
              <>
                On <AppLink to="/contacts">Contacts</AppLink>, open{" "}
                <Ui>Smart import</Ui> and choose the file.
              </>,
              <>
                Map your columns. Smart import reads your headers, suggests a
                match for each Obvious field, and can create a custom field
                on the fly for any column Obvious doesn't have. Columns you
                don't want, you skip.
              </>,
              <>
                Choose how to handle matches. Smart import checks existing
                contacts by email, so a re-import can update people instead
                of duplicating them.
              </>,
              <>
                Import. Contacts land with companies and tags created and
                linked along the way.
              </>,
            ]}
          />
        </Sect>
        <Sect title="The quick CSV importer">
          <P>
            The plain <Ui>Import</Ui> button on{" "}
            <AppLink to="/contacts">Contacts</AppLink> is faster but
            stricter: it expects exact column names (first_name, last_name,
            email_work, company, and so on) and you can download a sample CSV
            in the dialog to copy from. It creates a new contact for every
            row, so never re-run a file that already partially imported - use
            Smart import when in doubt.
          </P>
        </Sect>
        <Sect title="Cleaning up duplicates">
          <P>
            If duplicates do slip in, the <Ui>Duplicates</Ui> tool on the{" "}
            <AppLink to="/contacts">Contacts page</AppLink> finds likely
            matches and merges them, keeping every note, task, and touch from
            both records. Form fills always match by email, so day-to-day
            capture won't add duplicates on its own.
          </P>
        </Sect>
        <Sect title="JSON restore">
          <P>
            <AppLink to="/settings/import-export">Settings → Import</AppLink>{" "}
            is a different tool: a JSON restore that loads a full workspace
            shape (users, companies, contacts, notes, tasks) from a
            sample-matching file. It's for migrations and backups, not
            day-one spreadsheets.
          </P>
          <Tip>
            Two things before a big import: run a five-row test file first,
            and check your contact allowance at{" "}
            <AppLink to="/settings/billing">
              Settings → Plan &amp; billing
            </AppLink>{" "}
            - rows past your plan's contact cap fail to create (see{" "}
            <ArticleLink slug="settings-and-administration">
              Settings and administration
            </ArticleLink>
            ).
          </Tip>
        </Sect>
      </>
    ),
  },
];
